import { useEffect, useState } from "react";
import { FunctionField, ReferenceField, useNotify } from "react-admin";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { settings } from "app";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { accountRequests } from "api/accountApi";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import dataProvider from "server/dataProvider";
import { getFileFromUrl } from "server/components/DownloadFile";
import { EducationType, MOBILE_BREAKPOINT, STUDENTS } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";

const SearchModes = {
  Egn: "egn",
  Ean: "ean",
  FacultyNumber: "facultyNumber",
  BirthInfo: "birthInfo",
};

const SEARCH_FIELD_WIDTH = "220px";

const SearchStudentStep = ({ onCreate }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const [searchMode, setSearchMode] = useState(SearchModes.Egn);

  const [personId, setPersonId] = useState("");
  const [ean, setEan] = useState("");
  const [facultyNumber, setFacultyNumber] = useState("");
  const [specialityId, setSpecialityId] = useState(null);
  const [birthDate, setBirthDate] = useState("");
  const [birthCountryId, setBirthCountryId] = useState(null);
  const [primaryCitizenshipCountryId, setPrimaryCitizenshipCountryId] = useState(null);

  const [specialityChoices, setSpecialityChoices] = useState([]);
  const [countryChoices, setCountryChoices] = useState([]);

  const [matches, setMatches] = useState(null);
  const [selectedEducationId, setSelectedEducationId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dataProvider
      .customRequest(administrationRequests.getSpecialitiesSelect())
      .then(res => setSpecialityChoices(res.data))
      .catch(err => notifyError(err));
    dataProvider
      .customRequest(administrationRequests.getCountriesSelect())
      .then(res => setCountryChoices(res.data))
      .catch(err => notifyError(err));
  }, [notifyError]);

  // Any edit to the search criteria invalidates a previous search's results — the "Генерирай
  // документ" button(s) for a stale match set must not stay clickable.
  useEffect(() => {
    setMatches(null);
    setSelectedEducationId(null);
  }, [searchMode, personId, ean, facultyNumber, specialityId, birthDate, birthCountryId, primaryCitizenshipCountryId]);

  const handleSearchModeChange = mode => {
    setSearchMode(mode);
    setPersonId("");
    setEan("");
    setFacultyNumber("");
    setSpecialityId(null);
    setBirthDate("");
    setBirthCountryId(null);
    setPrimaryCitizenshipCountryId(null);
  };

  const isSearchDisabled = () => {
    switch (searchMode) {
      case SearchModes.Egn:
        return !personId;
      case SearchModes.Ean:
        return !ean;
      case SearchModes.FacultyNumber:
        return !facultyNumber || !specialityId;
      case SearchModes.BirthInfo:
        return !birthDate || !birthCountryId || !primaryCitizenshipCountryId;
      default:
        return true;
    }
  };

  const buildSearchParams = () => {
    switch (searchMode) {
      case SearchModes.Egn:
        return { personId };
      case SearchModes.Ean:
        return { ean };
      case SearchModes.FacultyNumber:
        return { facultyNumber, specialityId };
      case SearchModes.BirthInfo:
        return { dateOfBirth: birthDate, birthCountryId, primaryCitizenshipCountryId };
      default:
        return {};
    }
  };

  const specialityName = id => specialityChoices.find(s => s.id === id)?.name ?? "-";

  // Generates the report from an existing education record, skipping the manual entry form entirely.
  const generateReport = async studentEducationId => {
    const url = `${settings.gatewayApi}/${studentRequests.getStudentAcademicReport(studentEducationId)}`;
    await getFileFromUrl(url, "академична-справка", notify);
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getList(STUDENTS, {
        pagination: { page: 1, perPage: 50 },
        filter: { EducationType: EducationType.Regular, ...buildSearchParams() },
      });

      if (!data?.length) {
        notify("Няма намерен резултат! Продължете с попълване на празна форма.", { type: "warning" });
        onCreate();
        return;
      }

      // Birth info / ЕГН / EAN can all match more than one education (second speciality,
      // preparatory + regular, etc.) — never silently generate for whichever comes back first.

      if (data.length > 1) {
        setMatches(data);
        return;
      }

      await generateReport(data[0].studentEducationId);
    } catch (err) {
      notifyError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSelected = async () => {
    setLoading(true);
    try {
      await generateReport(selectedEducationId);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={settings.style.InputCardStyle}>
      <Card sx={{ width: isMobile ? "100%" : "760px" }}>
        <Typography
          variant="h5"
          sx={{ margin: "2rem 0rem 0rem", textAlign: "center" }}
        >
          Търсене на дипломиран студент
        </Typography>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          <FormControl>
            <RadioGroup
              value={searchMode}
              onChange={e => handleSearchModeChange(e.target.value)}
            >
              <FormControlLabel
                value={SearchModes.Egn}
                control={<Radio />}
                label="ЕГН (за българи)"
              />
              <FormControlLabel
                value={SearchModes.Ean}
                control={<Radio />}
                label="EAN (за българи и чужденци)"
              />
              <FormControlLabel
                value={SearchModes.FacultyNumber}
                control={<Radio />}
                label="Факултетен номер и специалност (за българи и чужденци)"
              />
              <FormControlLabel
                value={SearchModes.BirthInfo}
                control={<Radio />}
                label="Дата и място на раждане (за чужденци)"
              />
            </RadioGroup>
          </FormControl>
          {searchMode === SearchModes.Egn && (
            <TextField
              label="ЕГН"
              sx={{ width: SEARCH_FIELD_WIDTH }}
              value={personId}
              onChange={e => setPersonId(e.target.value)}
            />
          )}
          {searchMode === SearchModes.Ean && (
            <TextField
              label="EAN"
              sx={{ width: SEARCH_FIELD_WIDTH }}
              value={ean}
              onChange={e => setEan(e.target.value)}
            />
          )}
          {searchMode === SearchModes.FacultyNumber && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
              <TextField
                label="Факултетен номер"
                sx={{ width: SEARCH_FIELD_WIDTH }}
                value={facultyNumber}
                onChange={e => setFacultyNumber(e.target.value)}
              />
              <Autocomplete
                sx={{ width: SEARCH_FIELD_WIDTH }}
                options={specialityChoices}
                getOptionLabel={option => option?.name ?? ""}
                noOptionsText="Няма намерени резултати"
                value={specialityChoices.find(o => o.id === specialityId) ?? null}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(_, value) => setSpecialityId(value?.id ?? null)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Специалност"
                  />
                )}
              />
            </Box>
          )}
          {searchMode === SearchModes.BirthInfo && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
              <TextField
                label="Дата на раждане"
                type="date"
                sx={{ width: SEARCH_FIELD_WIDTH }}
                InputLabelProps={{ shrink: true }}
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
              />
              <Autocomplete
                sx={{ width: SEARCH_FIELD_WIDTH }}
                options={countryChoices}
                getOptionLabel={option => option?.nameBg ?? ""}
                noOptionsText="Няма намерени резултати"
                value={countryChoices.find(o => o.id === birthCountryId) ?? null}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(_, value) => setBirthCountryId(value?.id ?? null)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Държава на раждане"
                  />
                )}
              />
              <Autocomplete
                sx={{ width: SEARCH_FIELD_WIDTH }}
                options={countryChoices}
                getOptionLabel={option => option?.nameBg ?? ""}
                noOptionsText="Няма намерени резултати"
                value={countryChoices.find(o => o.id === primaryCitizenshipCountryId) ?? null}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                onChange={(_, value) => setPrimaryCitizenshipCountryId(value?.id ?? null)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Гражданство"
                  />
                )}
              />
            </Box>
          )}
          {!selectedEducationId && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <SecuredComponent permission={permissions.canGenerateAcademicRequestDocuments}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleSearch}
                  disabled={isSearchDisabled() || loading}
                >
                  Провери и генерирай документ
                </Button>
              </SecuredComponent>
            </Box>
          )}
          {matches && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Намерени са няколко съвпадения — изберете едно:</Typography>
              <RadioGroup
                value={selectedEducationId ?? ""}
                onChange={e => setSelectedEducationId(Number(e.target.value))}
              >
                {matches.map(m => (
                  <FormControlLabel
                    key={m.studentEducationId}
                    value={String(m.studentEducationId)}
                    control={<Radio />}
                    label={
                      <>
                        <ReferenceField
                          record={m}
                          source="userId"
                          reference={accountRequests.getUsersSelect()}
                          link={false}
                        >
                          <FunctionField
                            render={u => [u.firstName, u.secondName, u.lastName].filter(Boolean).join(" ")}
                          />
                        </ReferenceField>
                        {` | Специалност: ${specialityName(m.specialityId)} | Факултетен номер: ${m.facultyNumber ?? "-"} | Статус: ${m.educationStatusNameBg ?? "-"}`}
                      </>
                    }
                  />
                ))}
              </RadioGroup>
              <Box sx={{ display: "flex", gap: 2, margin: "0 auto" }}>
                <SecuredComponent permission={permissions.canGenerateAcademicRequestDocuments}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<FileDownloadIcon />}
                    disabled={!selectedEducationId || loading}
                    onClick={handleGenerateSelected}
                  >
                    Генерирай документ
                  </Button>
                </SecuredComponent>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SearchStudentStep;
